import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  evaluateTemplateValueCollection,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { CX, CY, HEIGHT, LABEL_GAP, R, WIDTH, axisHref, axisLinkLabel, clampRatio, polygon, sectorPath, vertex, wrapLabel } from './radarGeometry'

/**
 * Primitive GÉNÉRIQUE — un radar (toile d'araignée) en SVG en ligne.
 *
 * Pourquoi du JSX : les sommets d'un polygone régulier se calculent en
 * trigonométrie, et le YAML ne calcule pas.
 *
 * Pourquoi PAS `ChartJs` (qui sait faire un radar avec `RadialLinearScale`, et
 * dont la bibliothèque est de toute façon déjà dans le lot) : un canvas ne se
 * style pas en CSS — l'habillage devrait passer en JS, alors qu'ici le tracé ne
 * porte que des classes et AUCUNE couleur n'est écrite dans ce fichier ; et il
 * ne porte pas d'alternative textuelle native. Un radar de restitution est un
 * élément de PAGE, relevant du design system, pas un graphique de tableau de bord.
 *
 * Usage YAML :
 *   - type: RadarChart
 *     axes: ~~.quizResult.dimensions     # [{label, ratio}] — ratio de 0 à 1
 *     label: ~~.quizResult.summary       # alternative textuelle du graphique
 *     levels: 4
 *     attributes: { class: "mon-radar" }   # l'habillage vient de la recette CSS du site
 *
 * Options (toutes facultatives, le radar d'origine reste inchangé sans elles) :
 *   compareAxes:  ~~.site.radarPrevious   # [{ratio}] — seconde série, en
 *                                         # pointillé (l'analyse précédente).
 *                                         # Alignée par `key`, sinon par rang.
 *   axes[].tone:  good | warn | bad | none # ton de l'axe : classes
 *                                         # `radar-chart__dot--<ton>` et
 *                                         # `radar-chart__value--<ton>`
 *   axes[].value: "C (55)"                # valeur affichée sous l'intitulé
 *   axes[].toneLabel: "À surveiller"      # le ton EN TOUTES LETTRES (jamais la
 *                                         # couleur seule)
 *   linkTemplate: "/qualite/sites/:site/domaine?domain=:key"
 *   linkValues:   { site: ~~.id }         # jetons communs ; `:key` = l'axe
 *   linkHint:     "Ouvrir le rapport du domaine"
 *
 * `linkValues` est une MAP : elle est résolue par
 * `evaluateTemplateValueCollection` (un seul niveau).
 *
 * Accessibilité : sans lien, `role="img"` + `aria-label`. Avec `linkTemplate`,
 * chaque axe devient un lien SVG focalisable au clavier, à l'intitulé complet
 * (« Sécurité : 87, bon. Ouvrir le rapport du domaine ») ; le graphique passe
 * alors en `role="group"`, une image ne pouvant pas contenir d'éléments
 * interactifs. Le graphique ne doit JAMAIS porter seul une information — les
 * mêmes valeurs sont listées en toutes lettres à côté.
 */

export const RadarChart = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const raw = resolve(props.axes)
  const axes = Array.isArray(raw) ? raw : []
  const levels = Number(resolve(props.levels) ?? 4)
  const label = resolve(props.label) ?? ''
  const rawCompare = resolve(props.compareAxes)
  const compareAxes = Array.isArray(rawCompare) ? rawCompare : []
  const linkTemplate = resolve(props.linkTemplate)
  const linkValues = evaluateTemplateValueCollection({
    valueToEvaluate: props.linkValues ?? {},
    globalDataContext,
    templateContext,
  }) ?? {}
  const linkHint = resolve(props.linkHint) ?? ''
  const linked = typeof linkTemplate === 'string' && linkTemplate !== ''
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})

  // Trois axes au minimum : en dessous, un « polygone » est un segment.
  if (axes.length < 3) return null

  const n = axes.length
  // Le sommet d'une valeur nulle est ramené sur un rayon minuscule : sans lui,
  // un profil à zéro s'effondre sur le centre et le tracé disparaît.
  const values = axes.map((a, i) => vertex(i, n, R * Math.max(clampRatio(a.ratio), 0.03)))
  // Série de comparaison : rapprochée par `key` quand les deux listes en
  // portent, sinon par rang. Un axe sans pendant n'a simplement pas de sommet.
  const compareOf = (axis, i) => {
    const byKey = axis.key != null ? compareAxes.find((c) => c && c.key === axis.key) : undefined
    return byKey ?? compareAxes[i]
  }
  const compared = compareAxes.length
    ? axes.map((a, i) => {
      const c = compareOf(a, i)
      return c && c.ratio != null ? vertex(i, n, R * Math.max(clampRatio(c.ratio), 0.03)) : null
    })
    : []
  const compareComplete = compared.length === n && compared.every(Boolean)

  // Un axe = son point et son intitulé (+ sa valeur). Lié, il est enveloppé
  // d'un <a> SVG : focalisable nativement, activable à Entrée, et porteur d'une
  // zone de clic qui couvre tout le secteur — viser un point de 9 px serait
  // inutilisable à la souris comme au doigt.
  const axisGroup = (axis, i) => {
    const v = values[i]
    const [x, y] = vertex(i, n, R + LABEL_GAP)
    const anchor = Math.abs(x - CX) < 12 ? 'middle' : x > CX ? 'start' : 'end'
    const tx = anchor === 'start' ? x - 6 : anchor === 'end' ? x + 6 : x
    const lines = wrapLabel(axis.label)
    const hasValue = axis.value != null && axis.value !== ''
    const y0 = y + 4 - (lines.length - 1 + (hasValue ? 1 : 0)) * 8
    const tone = axis.tone ? String(axis.tone) : ''
    const body = (
      <>
        <circle
          className={tone ? `radar-chart__dot radar-chart__dot--${tone}` : 'radar-chart__dot'}
          cx={v[0].toFixed(1)}
          cy={v[1].toFixed(1)}
          r="4.5"
        />
        {lines.map((line, li) => (
          <text
            key={li}
            className="radar-chart__label"
            x={tx.toFixed(0)}
            y={(y0 + li * 16).toFixed(0)}
            textAnchor={anchor}
          >
            {line}
          </text>
        ))}
        {hasValue ? (
          <text
            className={tone ? `radar-chart__value radar-chart__value--${tone}` : 'radar-chart__value'}
            x={tx.toFixed(0)}
            y={(y0 + lines.length * 16).toFixed(0)}
            textAnchor={anchor}
          >
            {String(axis.value)}
          </text>
        ) : null}
      </>
    )
    const key = axis.key ?? i
    const href = linked ? axisHref(linkTemplate, { ...linkValues, key: axis.key }) : null
    if (!href) return <g key={key}>{body}</g>
    return (
      <a
        key={key}
        href={href}
        className="radar-chart__link"
        aria-label={axisLinkLabel(axis, linkHint)}
        data-builder-allow-navigation=""
      >
        <path className="radar-chart__hit" d={sectorPath(i, n, R + LABEL_GAP + 34)} />
        {body}
      </a>
    )
  }

  return (
    <ActionDependant {...props}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role={linked ? 'group' : 'img'}
        aria-label={label || undefined}
        aria-hidden={label || linked ? undefined : 'true'}
        {...evaluatedAttrs}
      >
        {Array.from({ length: levels }, (_, i) => (i + 1) / levels).map((f) => (
          <polygon key={f} className="radar-chart__grid" points={polygon(n, R * f)} fill="none" />
        ))}
        {axes.map((axis, i) => {
          const [x, y] = vertex(i, n, R)
          return (
            <line key={axis.key ?? i} className="radar-chart__axis" x1={CX} y1={CY} x2={x.toFixed(1)} y2={y.toFixed(1)} />
          )
        })}
        <polygon
          className="radar-chart__area"
          points={values.map((v) => v.map((x) => x.toFixed(1)).join(',')).join(' ')}
          strokeLinejoin="round"
        />
        {compareComplete ? (
          <polygon
            className="radar-chart__compare"
            points={compared.map((v) => v.map((x) => x.toFixed(1)).join(',')).join(' ')}
            fill="none"
            strokeLinejoin="round"
          />
        ) : null}
        {axes.map(axisGroup)}
      </svg>
    </ActionDependant>
  )
}
